import type { RumErrorEvent } from '@datadog/browser-rum'
import { IS_DEV, IS_PREVIEW, IS_PROD } from '~/src/lib/constants'

export const IGNORED_ERROR_KEYWORDS = [
  'Image load timeout',
  'ResizeObserver loop completed with undelivered notifications',
  'Connection to Indexed Database server lost',
]

const shouldIgnoreError = (error: RumErrorEvent['error']): boolean => {
  const matchedKeyword = IGNORED_ERROR_KEYWORDS.some(
    (keyword) =>
      error.message &&
      error.message.toLowerCase().includes(keyword.toLowerCase())
  )

  return matchedKeyword
}

export const IS_TEST =
  process.env.NODE_ENV === 'test' ||
  process.env.TEST === 'true' ||
  !!process.env.TEST_DATABASE_URL

export const IS_DEPLOYED = IS_PREVIEW || IS_PROD

export async function initDatadog() {
  // Skip instrumentation in development
  if (IS_DEV) {
    return
  }

  // This id and token are safe to expose publicly, they are designed to be public
  // The defaults are the values for the web-dupe-com RUM application, and the values can be overridden via environment variables
  const APPLICATION_ID =
    process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID ||
    'be081dfc-aff5-4371-836b-388c3b5eaab4'
  const CLIENT_TOKEN =
    process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ||
    'pub786b07930ac076aa821e0a0074a5ba8b'

  const DD_SERVICE = process.env.NEXT_PUBLIC_DD_SERVICE || 'web-dupe-com'

  let DD_ENV =
    process.env.NEXT_PUBLIC_VERCEL_TARGET_ENV ||
    process.env.NEXT_PUBLIC_ENV ||
    'development'
  // In a deployed preview environment, set the env for consistency with api and other services
  if (process.env.NEXT_PUBLIC_VERCEL_GIT_PULL_REQUEST_ID) {
    DD_ENV = `dupe-com-pr-${process.env.NEXT_PUBLIC_VERCEL_GIT_PULL_REQUEST_ID}`
  }

  const DD_VERSION =
    process.env.NEXT_PUBLIC_DD_VERSION ||
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA

  // RUM sampling disabled to reduce costs - can be re-enabled via env var if needed
  const DD_RUM_SESSION_SAMPLE_RATE = parseInt(
    process.env.NEXT_PUBLIC_DD_RUM_SESSION_SAMPLE_RATE || '0',
    10
  )

  // Session replay remains disabled
  const DD_RUM_SESSION_REPLAY_SAMPLE_RATE = parseInt(
    process.env.NEXT_PUBLIC_DD_RUM_SESSION_REPLAY_SAMPLE_RATE || '0',
    10
  )

  // Trace sampling disabled to reduce costs - can be re-enabled via env var if needed
  const DD_TRACE_SAMPLE_RATE = parseInt(
    process.env.NEXT_PUBLIC_DD_TRACE_SAMPLE_RATE || '0',
    10
  )

  // With every sample rate at 0 (the current prod default) an initialized SDK
  // collects nothing — all sessions are unsampled and addError calls are
  // dropped — so skip downloading it entirely (~65 KB gz). Setting any
  // NEXT_PUBLIC_DD_* rate > 0 re-enables the SDK on the next deploy.
  if (
    DD_RUM_SESSION_SAMPLE_RATE <= 0 &&
    DD_RUM_SESSION_REPLAY_SAMPLE_RATE <= 0 &&
    DD_TRACE_SAMPLE_RATE <= 0
  ) {
    return
  }

  // Dynamic imports keep the RUM SDK out of the critical-path bundle; when
  // sampling is enabled it downloads as an async chunk after startup.
  const [{ datadogRum }, { reactPlugin }] = await Promise.all([
    import('@datadog/browser-rum'),
    import('@datadog/browser-rum-react'),
  ])

  // Initialize Datadog RUM
  datadogRum.init({
    applicationId: APPLICATION_ID,
    clientToken: CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: DD_SERVICE,
    env: DD_ENV,
    version: DD_VERSION,
    // TODO test these
    // allowedTrackingOrigins: [
    //   process.env.NEXT_PUBLIC_PUBLIC_URL as string,
    // ],
    // allowedTracingUrls: [
    //   process.env.NEXT_PUBLIC_DUPE_API_URL as string,
    // ],
    useSecureSessionCookie: IS_DEPLOYED,
    usePartitionedCrossSiteSessionCookie: IS_DEPLOYED,
    sessionSampleRate: DD_RUM_SESSION_SAMPLE_RATE,
    sessionReplaySampleRate: DD_RUM_SESSION_REPLAY_SAMPLE_RATE,
    traceSampleRate: DD_TRACE_SAMPLE_RATE,
    traceContextInjection: 'sampled',
    trackUserInteractions: false,
    trackResources: false,
    trackLongTasks: false,
    defaultPrivacyLevel: 'mask-user-input',
    plugins: [reactPlugin({ router: false })],
    beforeSend: (event) => {
      // discard a RUM error if its message includes a keyword in IGNORED_ERROR_KEYWORDS
      if (event.type === 'error' && shouldIgnoreError(event.error)) {
        return false
      }

      // Otherwise, include as-is
      return true
    },
  })
}

// Swallow failures: a rejected dynamic import or datadogRum.init() must not
// surface as an unhandled promise rejection (console noise + a
// best-practices ding) — instrumentation failing is never worth breaking the
// page over.
initDatadog().catch(() => {})
